import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
    Chip,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { FieldConfig } from "./TaxReturnWebforms/types/formConfig";
import Button from "../atoms/Button";

interface FieldEditDialogProps {
    field: FieldConfig | null;
    open: boolean;
    onClose: () => void;
    onSave: (updated: FieldConfig) => void;
    onDelete?: () => void;
    canDelete: boolean;
}

const FieldEditDialog = ({
    field,
    open,
    onClose,
    onSave,
    onDelete,
    canDelete,
}: FieldEditDialogProps) => {
    const [fieldPath, setFieldPath] = useState<string>("");
    const [label, setLabel] = useState<string>("");
    const [important, setImportant] = useState<boolean>(false);
    const [helperText, setHelperText] = useState<string>("");
    const [width, setWidth] = useState<string>("");

    useEffect(() => {
        if (field) {
            setFieldPath(field.fieldPath || "");
            setLabel(typeof field.label === "string" ? field.label : "");
            setImportant(field.important || false);
            setHelperText(field.helperText || "");
            setWidth(field.width?.toString() || "");
        }
    }, [field]);

    const handleSave = () => {
        if (!field) return;

        const updated = {
            ...field,
            fieldPath,
            label,
            important: important || undefined,
            helperText: helperText || undefined,
            width: width ? (Number.isNaN(Number(width)) ? width : Number(width)) : undefined,
        } as FieldConfig;

        // Clean up undefined values
        if (!updated.important) delete updated.important;
        if (!updated.helperText) delete updated.helperText;
        if (!updated.width) delete updated.width;

        onSave(updated);
        onClose();
    };

    const handleDelete = () => {
        if (onDelete) {
            onDelete();
            onClose();
        }
    };

    if (!field) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Edit Field
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {/* Field type (read-only) */}
                    {field.type && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Type:
                            </Typography>
                            <Chip
                                label={field.type}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        </Box>
                    )}

                    {/* Editable fields */}
                    <TextField
                        label="Field Path"
                        value={fieldPath}
                        onChange={(e) => setFieldPath(e.target.value)}
                        fullWidth
                        size="small"
                        required
                        slotProps={{
                            input: {
                                sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                            },
                        }}
                        helperText="The path to extract this field from the data (e.g., 'income.wages')"
                    />

                    <TextField
                        label="Label"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        fullWidth
                        size="small"
                        required
                    />

                    <TextField
                        label="Helper Text"
                        value={helperText}
                        onChange={(e) => setHelperText(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="Optional helper text shown below the field"
                        multiline
                        rows={2}
                    />

                    <TextField
                        label="Width"
                        value={width}
                        onChange={(e) => setWidth(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="e.g., 100, 150, or '50%'"
                        helperText="Number (pixels) or string (e.g., '50%')"
                    />

                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={important}
                                onChange={(e) => setImportant(e.target.checked)}
                            />
                          )}
                        label={(
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography>Important field</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    (highlights with orange border)
                                </Typography>
                            </Box>
                          )}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Box>
                    {canDelete && onDelete && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleDelete}
                        >
                            Delete Field
                        </Button>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={!fieldPath.trim() || !label.trim()}>
                        Save
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default FieldEditDialog;
